import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, UserPlus, ArrowLeft, GraduationCap,
  Copy, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';

const RegisterChild = () => {
  const navigate = useNavigate();
  const { registerStudent } = useAuth();
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    year_group: '',
    key_stage: ''
  });

  const sidebarItems = [
    { path: '/parent', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/parent/register-child', label: 'Register Child', icon: UserPlus },
  ];

  const yearGroups = [
    { value: '1', label: 'Year 1', ks: 'ks1' },
    { value: '2', label: 'Year 2', ks: 'ks1' },
    { value: '3', label: 'Year 3', ks: 'ks2' },
    { value: '4', label: 'Year 4', ks: 'ks2' },
    { value: '5', label: 'Year 5', ks: 'ks2' },
    { value: '6', label: 'Year 6', ks: 'ks2' },
    { value: '7', label: 'Year 7', ks: 'ks3' },
    { value: '8', label: 'Year 8', ks: 'ks3' },
    { value: '9', label: 'Year 9', ks: 'ks3' },
    { value: '10', label: 'Year 10', ks: 'ks4' },
    { value: '11', label: 'Year 11', ks: 'ks4' },
  ];

  const handleYearChange = (value) => {
    const yearInfo = yearGroups.find(y => y.value === value);
    setFormData({
      ...formData,
      year_group: parseInt(value),
      key_stage: yearInfo?.ks || 'ks2'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await registerStudent(formData);
      setCredentials(result.login_credentials);
      toast.success('Child registered successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register child');
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = () => {
    const text = `Username: ${credentials.email}\nPassword: ${credentials.password}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Credentials copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (credentials) {
    return (
      <ProLayout title="Child Registered!" sidebarItems={sidebarItems}>
        <div className="max-w-lg mx-auto" data-testid="registration-success">
          <div className="pro-card text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">
              {formData.first_name} is Registered!
            </h2>
            <p className="text-slate-600 mb-6">
              Save these login credentials for your child to access their learning portal
            </p>

            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-700">Login Credentials</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyCredentials}
                  data-testid="copy-credentials"
                >
                  {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-slate-500">Username</p>
                  <p className="font-mono text-sm text-slate-800 bg-white p-2 rounded border">
                    {credentials.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Password</p>
                  <p className="font-mono text-sm text-slate-800 bg-white p-2 rounded border">
                    {credentials.password}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> Please save these credentials securely. Your child will need them to log in to their learning portal.
              </p>
            </div>

            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setCredentials(null);
                  setFormData({ first_name: '', last_name: '', year_group: '', key_stage: '' });
                }}
                data-testid="register-another"
              >
                Register Another Child
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/parent')}
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Register Child" sidebarItems={sidebarItems}>
      <div className="max-w-lg mx-auto" data-testid="register-child-form">
        <Link to="/parent" className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="pro-card">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <UserPlus className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-800">Register Your Child</h2>
            <p className="text-slate-500 text-sm mt-1">
              Add your child to start their learning journey
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="Enter first name"
                  required
                  data-testid="child-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Enter last name"
                  required
                  data-testid="child-last-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="year_group">Year Group</Label>
              <Select 
                value={formData.year_group?.toString()} 
                onValueChange={handleYearChange}
              >
                <SelectTrigger data-testid="child-year-group">
                  <SelectValue placeholder="Select year group" />
                </SelectTrigger>
                <SelectContent>
                  {yearGroups.map(year => (
                    <SelectItem key={year.value} value={year.value}>
                      {year.label} ({year.ks.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.key_stage && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Key Stage:</strong> {formData.key_stage.toUpperCase()} curriculum will be assigned based on Year {formData.year_group}
                </p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || !formData.first_name || !formData.last_name || !formData.year_group}
              data-testid="submit-register-child"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register Child
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </ProLayout>
  );
};

export default RegisterChild;
